import { useId, useState } from 'react'
import { api, type ActivityApproval } from '../../api/client'
import { useAuth } from '../../context/AuthContext'

/**
 * ADR-203 D7 — an activity run's approval task. A pending approval offers
 * Approve and Reject to an operator or fleet owner; everyone else sees it
 * read-only. The decision is bound to the preview shown here: the request
 * carries its hash, and the server refuses a decision on any other preview.
 */
export function ApprovalCard({ approval, onDecided }: { approval: ActivityApproval; onDecided: () => void }) {
  const { user } = useAuth()
  const canDecide = approval.status === 'pending' && approval.can_decide === true
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const noteId = useId()

  async function decide(decision: 'approve' | 'reject') {
    setBusy(decision)
    setError(null)
    try {
      const trimmedNote = note.trim()
      if (trimmedNote) {
        await api.decideActivityApproval(approval.id, decision, approval.preview_sha256, trimmedNote)
      } else {
        await api.decideActivityApproval(approval.id, decision, approval.preview_sha256)
      }
      onDecided()
    } catch (e) {
      setError(conflictMessage(e))
      // Whatever happened, show the approval as it now is.
      onDecided()
    } finally {
      setBusy(null)
    }
  }

  const title = approval.title || approval.step_key
  return (
    <div
      className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-gray-800"
      data-testid="approval-card"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-rose-800">✋ {title}</p>
        <StatusBadge approval={approval} />
      </div>
      <pre className="mt-1.5 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-white p-2 text-xs text-gray-700 border border-rose-100">
        {approval.preview}
      </pre>
      {approval.approvers && approval.approvers.length > 0 && (
        <p className="mt-2 text-xs text-gray-600">
          Approvers: {approval.approvers.map(approver => (
            approval.approver_mode === 'directors'
              ? approver.name
              : `${approver.name} (${approver.role})`
          )).join(', ')}{approval.approver_mode === 'directors' ? ' (any authorised director)' : ''}
        </p>
      )}
      {approval.notices && approval.notices.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-gray-600" aria-label="Approval notices">
          {approval.notices.map(notice => {
            const name = notice.name || notice.actor_key
            const prefix = notice.kind === 'escalation' ? 'Escalated to' : 'Sent to'
            const when = notice.sent_at ? ` at ${new Date(notice.sent_at).toLocaleString()}` : ''
            const status = notice.status === 'sent'
              ? `${prefix} ${name} in Slack${when}`
              : notice.status === 'pending'
                ? `Pending notification to ${name}`
                : `Could not notify ${name}: ${notice.last_error || notice.status}`
            return <li key={`${notice.kind}:${notice.actor_key}`}>{status}</li>
          })}
        </ul>
      )}
      {approval.board_status && approval.board_detail && (
        <p className="mt-1 text-xs text-amber-800">
          <span>{approval.board_status === 'unavailable' ? 'Board approval unavailable:' : 'Board approval:'}</span>{' '}
          <span>{approval.board_detail}</span>
        </p>
      )}
      {approval.status === 'pending' && (
        canDecide ? (
          <div>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => decide('approve')}
                disabled={busy !== null}
                className="rounded bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {busy === 'approve' ? 'Approving…' : 'Approve'}
              </button>
              <button
                type="button"
                onClick={() => decide('reject')}
                disabled={busy !== null}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {busy === 'reject' ? 'Rejecting…' : 'Reject'}
              </button>
              <span className="text-xs text-gray-500">A chat reply does not approve this. Nothing is written until it is approved here.</span>
            </div>
            <div className="mt-2">
              <label htmlFor={noteId} className="text-xs font-medium text-gray-700">Note (optional)</label>
              <textarea
                id={noteId}
                value={note}
                onChange={event => setNote(event.target.value)}
                maxLength={2000}
                rows={2}
                disabled={busy !== null}
                aria-describedby={`${noteId}-count`}
                className="mt-1 block w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm disabled:opacity-50"
              />
              <p id={`${noteId}-count`} className="mt-0.5 text-right text-xs text-gray-500">{note.length} / 2000</p>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-xs text-gray-500">
            {approval.approver_mode === null
              ? 'This request was parked before approver checks existed. Start the activity again to ask its approvers.'
              : (user.fleet_role === 'operator' || user.fleet_role === 'fleet_owner')
                ? 'Only the listed approvers can decide this request.'
                : 'Waiting for an operator to approve or reject this run.'}
          </p>
        )
      )}
      {approval.status !== 'pending' && approval.decision_note && (
        <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700">{approval.decision_note}</p>
      )}
      {error && <p className="mt-1.5 text-xs text-red-600" role="alert">{error}</p>}
    </div>
  )
}

function StatusBadge({ approval }: { approval: ActivityApproval }) {
  const by = approval.decided_by ? ` by ${approval.decided_by}` : ''
  const route = approval.decided_route ? ` via ${routeLabel(approval.decided_route)}` : ''
  switch (approval.status) {
    case 'approved':
      return <span className="text-xs font-medium text-emerald-700">Approved{by}{route}</span>
    case 'rejected':
      return <span className="text-xs font-medium text-gray-600">Rejected{by}{route}</span>
    case 'superseded':
      return <span className="text-xs font-medium text-gray-500">Closed: the run was replaced or has ended</span>
    default:
      return <span className="text-xs font-medium text-rose-700">Waiting for approval</span>
  }
}

function routeLabel(route: NonNullable<ActivityApproval['decided_route']>): string {
  switch (route) {
    case 'mc': return 'Mission Control'
    case 'slack': return 'Slack'
    case 'asana_stage': return 'Asana board'
    case 'asana_field': return 'Asana field'
  }
}

function conflictMessage(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e)
  if (msg.startsWith('HTTP 400')) {
    try {
      const body = JSON.parse(msg.slice(msg.indexOf('{'))) as { error?: string; message?: string }
      if (body.error === 'invalid_note' && typeof body.message === 'string') return body.message
    } catch { /* Fall through to the ordinary request error. */ }
  }
  if (msg.startsWith('HTTP 403') && msg.includes('not_an_approver')) return 'Only the listed approvers can decide this request.'
  if (msg.startsWith('HTTP 409')) {
    if (msg.includes('preview_mismatch')) return 'The preview changed since this page loaded. Review the current preview and decide again.'
    if (msg.includes('no_approver_policy')) return 'This request was parked before approver checks existed. Start the activity again to ask its approvers.'
    return 'This approval was already decided, or a newer run replaced it.'
  }
  if (msg.startsWith('HTTP 404')) return 'This approval no longer exists.'
  return `The decision was not recorded: ${msg}`
}
